import React, { useState, useEffect } from "react";
import axios from "axios";
import InfoOutlineIcon from "@mui/icons-material/InfoOutline";
import {
  Button,
  TextField,
  Tooltip,
  Container,
  Typography,
  InputAdornment,
  Box,
  Table,
  Modal,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  styled,
  IconButton,
  Stack,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  useTheme,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Close as CloseIcon,
  KeyboardDoubleArrowLeft as ChevronLeft,
  KeyboardDoubleArrowRight as ChevronRight,
} from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { useNavigate } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const CustomTableScrollbar = styled("div")(({ theme }) => ({
  "&::-webkit-scrollbar": {
    height: "6px",
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.2)"
        : "rgba(0,0,0,0.2)",
    borderRadius: "3px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.3)"
        : "rgba(0,0,0,0.3)",
  },
  scrollbarWidth: "thin",
  scrollbarColor: `${theme.palette.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)"} transparent`,
}));

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const PaymentMode = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = [5, 10, 25, 50];
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [showUserGuideModal, setShowUserGuideModal] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddPaymentModeModal, setShowAddPaymentModeModal] = useState(false);
  const [showEditPaymentModeModal, setShowEditPaymentModeModal] =
    useState(false);
  const [paymentModeName, setPaymentModeName] = useState("");
  const [selectedPaymentMode, setSelectedPaymentMode] = useState({
    PaymentModeId: null,
    PaymentModeName: "",
  });
  const [paymentModes, setPaymentModes] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all payment modes
  const fetchPaymentModes = async () => {
    setLoading(true);
    try {
      const accessToken = localStorage.getItem("accessToken");
      const response = await axios.get(
        `${API_BASE_URL}/api/master/payment-methods/`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      const mappedPaymentModes = response.data.map((pm) => ({
        PaymentModeId: pm.id,
        PaymentModeName: pm.name,
      }));
      setPaymentModes(mappedPaymentModes);
      setFilteredData(mappedPaymentModes);
    } catch (err) {
      setError(err.message);
      setSnackbarSeverity("error");
      setSnackbarMessage("Failed to fetch payment modes.");
      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem("accessToken");
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  // Add a new payment mode
  const handleSave = async (e) => {
    e.preventDefault();
    if (!paymentModeName) {
      setSnackbarSeverity("error");
      setSnackbarMessage("Payment Mode is required.");
      setOpenSnackbar(true);
      return;
    }
    try {
      const accessToken = localStorage.getItem("accessToken");
      const response = await axios.post(
        `${API_BASE_URL}/api/master/payment-methods/`,
        { name: paymentModeName },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      const newPaymentMode = {
        PaymentModeId: response.data.id,
        PaymentModeName: response.data.name,
      };
      setPaymentModes([...paymentModes, newPaymentMode]);
      setFilteredData([...filteredData, newPaymentMode]);
      setShowAddPaymentModeModal(false);
      setSnackbarMessage("Payment mode added successfully!");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
      setPaymentModeName("");
    } catch (err) {
      setError(err.message);
      setSnackbarSeverity("error");
      setSnackbarMessage("Failed to add payment mode.");
      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem("accessToken");
        navigate("/login");
      }
    }
  };

  // Delete a payment mode
  const handleDelete = async () => {
    try {
      const accessToken = localStorage.getItem("accessToken");
      await axios.delete(
        `${API_BASE_URL}/api/master/payment-methods/${selectedPaymentMode.PaymentModeId}/`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      const updatedPaymentModes = paymentModes.filter(
        (pm) => pm.PaymentModeId !== selectedPaymentMode.PaymentModeId,
      );
      setPaymentModes(updatedPaymentModes);
      setFilteredData(updatedPaymentModes);
      setShowDeleteModal(false);
      setSnackbarMessage("Payment mode deleted successfully!");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
    } catch (err) {
      setError(err.message);
      setSnackbarSeverity("error");
      setSnackbarMessage("Failed to delete payment mode.");
      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem("accessToken");
        navigate("/login");
      }
    }
  };

  // Edit a payment mode
  const handleUpdate = async () => {
    if (!paymentModeName) {
      setSnackbarSeverity("error");
      setSnackbarMessage("Payment Mode is required.");
      setOpenSnackbar(true);
      return;
    }
    try {
      const accessToken = localStorage.getItem("accessToken");
      const response = await axios.put(
        `${API_BASE_URL}/api/master/payment-methods/${selectedPaymentMode.PaymentModeId}/`,
        { name: paymentModeName },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      const updatedPaymentMode = {
        PaymentModeId: response.data.id,
        PaymentModeName: response.data.name,
      };
      const updatedPaymentModes = paymentModes.map((pm) =>
        pm.PaymentModeId === selectedPaymentMode.PaymentModeId
          ? updatedPaymentMode
          : pm,
      );
      setPaymentModes(updatedPaymentModes);
      setFilteredData(updatedPaymentModes);
      setShowEditPaymentModeModal(false);
      setSnackbarMessage("Payment mode updated successfully!");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
      setPaymentModeName("");
    } catch (err) {
      setError(err.message);
      setSnackbarSeverity("error");
      setSnackbarMessage("Failed to update payment mode.");
      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem("accessToken");
        navigate("/login");
      }
    }
  };

  const handleAddPaymentMode = () => {
    setPaymentModeName("");
    setShowAddPaymentModeModal(true);
  };

  const handleEdit = (pm) => {
    setSelectedPaymentMode({
      PaymentModeId: pm.PaymentModeId,
      PaymentModeName: pm.PaymentModeName,
    });
    setPaymentModeName(pm.PaymentModeName);
    setShowEditPaymentModeModal(true);
  };

  const handleShowDeleteModal = (PaymentModeId, PaymentModeName) => {
    setSelectedPaymentMode({ PaymentModeId, PaymentModeName });
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleReset = () => {
    setPaymentModeName("");
  };

  const handleCancel = () => {
    setShowEditPaymentModeModal(false);
    setPaymentModeName("");
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      navigate("/login");
    } else {
      fetchPaymentModes();
    }
  }, [navigate]);

  useEffect(() => {
    setFilteredData(
      paymentModes.filter((pm) =>
        pm.PaymentModeName.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    );
  }, [searchTerm, paymentModes]);

  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage,
  );

  return (
    <Container>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
          p: 2,
          boxShadow: "none",
          maxWidth: "100%",
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
          }}
        >
          <TextField
            variant="outlined"
            placeholder="Search by Payment Mode"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      color: theme.palette.primary.main,
                      marginLeft: 1.5,
                      fontSize: 24,
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm("")}
                    sx={{
                      color: "grey",
                      marginRight: 1,
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                borderRadius: "30px",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.15)"
                    : "rgba(255, 255, 255, 1)",
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0 2px 8px rgba(0, 0, 0, 0.3)"
                    : "0 2px 8px rgba(0, 0, 0, 0.1)",
                "& .MuiOutlinedInput-notchedOutline": {
                  border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.1)"}`,
                },
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.2)"
                      : "rgba(240, 240, 240, 1)",
                  boxShadow:
                    theme.palette.mode === "dark"
                      ? "0 2px 12px rgba(0, 0, 0, 0.4)"
                      : "0 2px 12px rgba(0, 0, 0, 0.15)",
                },
                "&.Mui-focused": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.25)"
                      : "rgba(255, 255, 255, 1)",
                  boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: `2px solid ${theme.palette.primary.main}`,
                  },
                },
                paddingLeft: 1.5,
                height: "50px",
                transition: "all 0.3s ease",
              },
            }}
            sx={{
              width: 350,
              maxWidth: "100%",
              "& .MuiInputBase-input": {
                padding: "10px 14px",
                color: theme.palette.text.primary,
                fontWeight: 500,
              },
            }}
          />
          <Button
            variant="contained"
            onClick={handleAddPaymentMode}
            sx={{
              ml: 2,
              width: "190px",
              borderRadius: "10px",
              height: "60px",
              padding: "10px 0",
              fontWeight: "bold",
              backgroundColor: theme.palette.primary.main,
              "&:hover": { backgroundColor: theme.palette.primary.dark },
            }}
            startIcon={<AddIcon />}
          >
            ADD PAYMENT MODE
          </Button>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <IconButton
            onClick={() => setShowUserGuideModal(true)}
            sx={{
              width: 60,
              height: 60,
              borderRadius: "50%",
              minWidth: 0,
            }}
          >
            <InfoOutlineIcon sx={{ fontSize: 30 }} />
          </IconButton>
        </Box>
      </Box>

      <Paper
        elevation={3}
        sx={{
          paddingBottom: 0,
          maxWidth: "100%",
          boxSizing: "border-box",
          mx: "auto",
          backgroundColor: theme.palette.background.paper,
          borderRadius: "16px",
        }}
      >
        <CustomTableScrollbar sx={{ width: "100%", overflowX: "auto" }}>
          <TableContainer sx={{ width: "100%", borderRadius: "16px" }}>
            <Table
              sx={{
                "& .MuiTableHead-root .MuiTableCell-root": {
                  fontWeight: "bold",
                  backgroundColor: theme.palette.primary.main,
                  color: "white",
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "1.1rem" }}>
                    S.No
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "1.1rem" }}>
                    Payment Mode
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: theme.palette.primary.dark,
                      fontSize: "1.1rem",
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : paginatedData.length > 0 ? (
                  paginatedData.map((pm, index) => (
                    <TableRow
                      key={pm.PaymentModeId}
                      sx={{
                        backgroundColor:
                          index % 2 === 0
                            ? theme.palette.background.paper
                            : theme.palette.background.paper,
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.04)",
                        },
                      }}
                    >
                      <TableCell sx={{ fontSize: "1rem" }}>
                        {index + 1}
                      </TableCell>
                      <TableCell sx={{ fontSize: "1rem" }}>
                        {pm.PaymentModeName}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          onClick={() => handleEdit(pm)}
                          sx={{
                            color: theme.palette.primary.light,
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.04)",
                            },
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() =>
                            handleShowDeleteModal(
                              pm.PaymentModeId,
                              pm.PaymentModeName,
                            )
                          }
                          sx={{
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.04)",
                            },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow
                    sx={{
                      backgroundColor: theme.palette.background.paper,
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    <TableCell
                      colSpan={3}
                      align="center"
                      sx={{ fontSize: "1rem", padding: "20px", wordSpacing: 7 }}
                    >
                      No Data Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CustomTableScrollbar>
      </Paper>

      {filteredData.length > 0 && (
        <Box
          sx={{
            maxWidth: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 1,
            p: 2,
            mx: "auto",
            pt: 2,
          }}
        >
          <Typography variant="body2" color={theme.palette.primary.main}>
            Page <strong>{page}</strong> of{" "}
            <strong>{Math.ceil(filteredData.length / rowsPerPage)}</strong>
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 1}
              sx={{
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
                "&.Mui-disabled": {
                  opacity: 0.3,
                },
                borderRadius: "50%",
                width: "36px",
                height: "36px",
              }}
            >
              <ChevronLeft />
            </IconButton>
            {[...Array(Math.ceil(filteredData.length / rowsPerPage))].map(
              (_, index) => (
                <IconButton
                  key={index + 1}
                  onClick={() => handleChangePage(null, index + 1)}
                  sx={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    backgroundColor:
                      page === index + 1
                        ? theme.palette.primary.main
                        : "transparent",
                    color:
                      page === index + 1
                        ? theme.palette.primary.contrastText
                        : theme.palette.text.primary,
                    "&:hover": {
                      backgroundColor:
                        page === index + 1
                          ? theme.palette.primary.dark
                          : theme.palette.action.hover,
                    },
                  }}
                >
                  {index + 1}
                </IconButton>
              ),
            )}
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredData.length / rowsPerPage)}
              sx={{
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
                "&.Mui-disabled": {
                  opacity: 0.3,
                },
                borderRadius: "50%",
                width: "36px",
                height: "36px",
              }}
            >
              <ChevronRight />
            </IconButton>
            <FormControl size="small" sx={{ minWidth: 100, ml: 1 }}>
              <InputLabel
                id="rows-per-page-label"
                sx={{ color: theme.palette.text.primary }}
              >
                Rows
              </InputLabel>
              <Select
                labelId="rows-per-page-label"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                label="Rows"
                sx={{
                  height: "36px",
                  color: theme.palette.text.primary,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.text.primary,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                {rowsPerPageOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Delete Modal */}
      <Modal
        open={showDeleteModal}
        onClose={handleCloseDeleteModal}
        BackdropProps={{ style: { backgroundColor: "rgba(0, 0, 0, 0.8)" } }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              padding: "12px 16px",
            }}
          >
            <Typography
              id="delete-modal-title"
              variant="h6"
              sx={{ color: "#fff" }}
            >
              Delete Confirmation ⚠️
            </Typography>
          </Box>
          <Box sx={{ padding: "20px" }}>
            <Typography id="delete-modal-description" sx={{ mb: 2 }}>
              Are you sure you want to delete{" "}
              <strong>{selectedPaymentMode.PaymentModeName}</strong>?
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
              <Button
                onClick={handleCloseDeleteModal}
                variant="outlined"
                sx={{ mr: 1 }}
               data-app-cancel="true">
                Cancel
              </Button>
              <Button variant="contained" color="error" onClick={handleDelete}>
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Add Payment Mode Modal */}
      <Modal
        open={showAddPaymentModeModal}
        onClose={() => setShowAddPaymentModeModal(false)}
        BackdropProps={{ style: { backgroundColor: "rgba(0, 0, 0, 0.8)" } }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              padding: "12px 16px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" sx={{ color: "#fff", fontWeight: "bold" }}>
              Add Payment Mode
            </Typography>
            <IconButton
              onClick={() => setShowAddPaymentModeModal(false)}
              sx={{ color: "#fff" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ padding: "20px" }}>
            <TextField
              label="Payment Mode"
              variant="outlined"
              fullWidth
              value={paymentModeName}
              onChange={(e) => setPaymentModeName(e.target.value)}
              sx={{
                mb: 3,
                "& .MuiInputLabel-root": {
                  color: theme.palette.text.primary,
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: theme.palette.primary.main,
                },
              }}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Tooltip
                title={!paymentModeName ? "Enter Payment Mode" : ""}
                arrow
              >
                <span>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    sx={{
                      mr: 2,
                      backgroundColor: theme.palette.primary.main,
                      "&:hover": {
                        backgroundColor: theme.palette.primary.dark,
                      },
                    }}
                    disabled={!paymentModeName}
                  >
                    SAVE
                  </Button>
                </span>
              </Tooltip>
              <Button variant="contained" color="error" onClick={handleReset}>
                RESET
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Edit Payment Mode Modal */}
      <Modal
        open={showEditPaymentModeModal}
        onClose={handleCancel}
        BackdropProps={{ style: { backgroundColor: "rgba(0, 0, 0, 0.8)" } }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              padding: "12px 16px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" sx={{ color: "#fff", fontWeight: "bold" }}>
              Edit Payment Mode
            </Typography>
            <IconButton onClick={handleCancel} sx={{ color: "#fff" }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ padding: "20px" }}>
            <TextField
              label="Payment Mode"
              variant="outlined"
              fullWidth
              value={paymentModeName}
              onChange={(e) => setPaymentModeName(e.target.value)}
              sx={{
                mb: 3,
                "& .MuiInputLabel-root": {
                  color: theme.palette.text.primary,
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: theme.palette.primary.main,
                },
              }}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Tooltip
                title={!paymentModeName ? "Enter Payment Mode" : ""}
                arrow
              >
                <span>
                  <Button
                    variant="contained"
                    onClick={handleUpdate}
                    sx={{
                      mr: 2,
                      backgroundColor: theme.palette.primary.main,
                      "&:hover": {
                        backgroundColor: theme.palette.primary.dark,
                      },
                    }}
                    disabled={!paymentModeName}
                  >
                    UPDATE
                  </Button>
                </span>
              </Tooltip>
              <Button variant="contained" color="error" onClick={handleCancel}>
                CANCEL
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* User Guide Modal */}
      <Modal
        open={showUserGuideModal}
        onClose={() => setShowUserGuideModal(false)}
        BackdropProps={{ style: { backgroundColor: "rgba(0, 0, 0, 0.8)" } }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 800,
            bgcolor: "background.paper",
            borderRadius: "16px",
            boxShadow: 24,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              padding: "12px 16px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              position: "relative",
            }}
          >
            <Typography variant="h4" sx={{ color: "#fff", fontWeight: "bold" }}>
              Payment Mode Management - User Guide
            </Typography>
            <IconButton
              onClick={() => setShowUserGuideModal(false)}
              sx={{ color: "#fff", position: "absolute", right: 14 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <CustomTableScrollbar
            sx={{ maxHeight: "70vh", overflowY: "auto", padding: "20px" }}
          >
            {/* Section 1: Add Payment Mode */}
            <Typography
              variant="body1"
              sx={{
                fontWeight: "bold",
                color: theme.palette.primary.main,
                mb: 1,
              }}
            >
              ➤ Follow these steps to add a new payment mode:
            </Typography>
            <ul
              style={{
                marginLeft: "20px",
                lineHeight: "1.8",
                cursor: "pointer",
              }}
            >
              <li>
                Click the <strong>"ADD PAYMENT MODE"</strong> button at the top
                right to open the Add Payment Mode dialog.
              </li>
              <li>
                Enter the <strong>Payment Mode</strong> in the provided text
                field.
              </li>
              <li>
                Before clicking <strong>"SAVE"</strong>, review the entered
                information for accuracy.
              </li>
              <li>
                Click <strong>"SAVE"</strong> to add the payment mode. A success
                notification will confirm the addition.
              </li>
              <li>The new payment mode will appear in the list.</li>
              <li>
                To clear the form without saving, click <strong>"RESET"</strong>
                .
              </li>
            </ul>
            <Typography
              variant="body2"
              sx={{ mt: 2, color: theme.palette.text.primary }}
            >
              ⚠️ Note: Payment Mode is a required field.
            </Typography>

            {/* Section 2: Edit Payment Mode */}
            <Typography
              variant="body1"
              sx={{
                fontWeight: "bold",
                color: theme.palette.primary.main,
                mt: 3,
                mb: 1,
              }}
            >
              ➤ Follow these steps to update an existing payment mode:
            </Typography>
            <ul
              style={{
                marginLeft: "20px",
                lineHeight: "1.8",
                cursor: "pointer",
              }}
            >
              <li>Locate the payment mode you want to update in the list.</li>
              <li>
                Click the <strong>Edit</strong> icon (pencil) in the Action
                column to open the Edit Payment Mode dialog.
              </li>
              <li>
                Update the <strong>Payment Mode</strong> as needed.
              </li>
              <li>
                Before clicking <strong>"UPDATE"</strong>, ensure the Payment
                Mode field is filled.
              </li>
              <li>
                Click <strong>"UPDATE"</strong> to save your changes. A success
                notification will confirm the update.
              </li>
              <li>
                To exit without saving, click <strong>"CANCEL"</strong>.
              </li>
            </ul>
            <Typography
              variant="body2"
              sx={{ mt: 2, color: theme.palette.text.primary }}
            >
              ⚠️ Note: Payment Mode is a required field.
            </Typography>

            {/* Section 3: Delete Payment Mode */}
            <Typography
              variant="body1"
              sx={{
                fontWeight: "bold",
                color: theme.palette.primary.main,
                mt: 3,
                mb: 1,
              }}
            >
              ➤ Follow these steps to delete a payment mode:
            </Typography>
            <ul
              style={{
                marginLeft: "20px",
                lineHeight: "1.8",
                cursor: "pointer",
              }}
            >
              <li>Find the payment mode you want to delete in the list.</li>
              <li>
                Click the <strong>Delete</strong> icon (trash) in the Action
                column.
              </li>
              <li>
                Confirm the deletion by clicking <strong>"Yes, Delete"</strong>{" "}
                in the confirmation dialog.
              </li>
              <li>
                The payment mode will be removed from the list, and a success
                notification will appear.
              </li>
            </ul>
            <Typography
              variant="body2"
              sx={{ mt: 2, color: theme.palette.text.primary }}
            >
              ⚠️ Note: Deleted payment modes cannot be recovered. Proceed with
              caution.
            </Typography>

            {/* Section 4: Search Payment Modes */}
            <Typography
              variant="body1"
              sx={{
                fontWeight: "bold",
                color: theme.palette.primary.main,
                mt: 3,
                mb: 1,
              }}
            >
              ➤ Follow these steps to search for a payment mode:
            </Typography>
            <ul
              style={{
                marginLeft: "20px",
                lineHeight: "1.8",
                cursor: "pointer",
              }}
            >
              <li>
                Use the search bar at the top left of the payment mode list.
              </li>
              <li>
                Enter the Payment Mode (or part of it) to filter the table.
              </li>
              <li>
                The table will update automatically to show matching payment
                modes.
              </li>
            </ul>
            <Typography
              variant="body2"
              sx={{ mt: 2, color: theme.palette.text.primary }}
            >
              ⚠️ Note: The search is case-insensitive and filters by Payment
              Mode.
            </Typography>

            {/* Section 5: Pagination */}
            <Typography
              variant="body1"
              sx={{
                fontWeight: "bold",
                color: theme.palette.primary.main,
                mt: 3,
                mb: 1,
              }}
            >
              ➤ Follow these steps to navigate the payment mode list:
            </Typography>
            <ul
              style={{
                marginLeft: "20px",
                lineHeight: "1.8",
                cursor: "pointer",
              }}
            >
              <li>
                Use the pagination controls at the bottom to navigate through
                pages of payment modes.
              </li>
              <li>
                Click the left (<strong>&lt;&lt;</strong>) or right (
                <strong>&gt;&gt;</strong>) arrows to move between pages.
              </li>
              <li>
                Select a specific page number to jump directly to that page.
              </li>
              <li>
                Adjust the number of rows per page using the dropdown menu
                (e.g., 5, 10, 25, 50 rows).
              </li>
            </ul>
            <Typography
              variant="body2"
              sx={{ mt: 2, color: theme.palette.text.primary }}
            >
              ⚠️ Note: The table displays only the selected number of rows per
              page.
            </Typography>
          </CustomTableScrollbar>
        </Box>
      </Modal>
    </Container>
  );
};

export default PaymentMode;
